import { FormControl, Grid, InputLabel, MenuItem, NativeSelect, Select, Typography, useMediaQuery } from '@mui/material';
import { getYear } from 'date-fns';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { datePartsToDate, IDateParts, splitDateToParts } from '../date-util';
import { ErrorMessage } from 'formik';

interface IProps {
  theme: any;
  dateFieldName: string;
  value: string;
  errors: any;
  touched: any;
  readonly: boolean;
  setFieldValue: (name: string, value: any, shouldValidate?: boolean) => void;
}

interface IVal {
  name: string;
  value: string;
}

export function YearMonthDay(props: IProps) {
  const { theme } = props;
  const spacing = theme.spacing;

  // TODO - upgrade: fix 'any' fudge and keep ErrorMessage happy
  const cssClasses: any = {
    fieldLabel: {
      zIndex: 10,
      backgroundColor: '#fff',
    },
    fieldLabelError: {
      zIndex: 10,
      backgroundColor: '#fff',
      color: '#B00020',
    },
    yearField: {
      width: '90px',
      marginRight: spacing(1),
    },
    monthField: {
      width: '130px',
      marginRight: spacing(1),
    },
    dateField: {
      width: '70px',
    },
    genderField: {
      width: '100%',
    },
    caption: {
      width: '100%',
    },
    selectComponent: {
      '& legend': {
        visibility: 'hidden',
        maxWidth: 0.01,
      },
    },
    errorBorder: {
      border: '1px solid #B00020',
    },
    errorMessage: {
      color: '#B00020',
    },
    touched: {
      '& $label': {
        color: 'inherit !important',
      },
      '& [id^="date"]': {
        color: 'inherit !important',
      },
      '& [class*="notchedOutline"]': {
        borderColor: 'inherit !important',
      },
    },
  };
  const { readonly, dateFieldName, value, errors, touched, setFieldValue } = props;
  const [dateParts, setDateParts] = useState<IDateParts>({ dateYear: '', dateMonth: '', dateDate: '' });

  const year = getYear(new Date());
  const yearRange = { start: year - 100, stop: year };
  const showSmDn = useMediaQuery(theme.breakpoints.down('sm'));

  const createNumberSeries = (start: number, stop: number, pad?: number) => {
    let items: any[] = [];
    if (showSmDn) {
      items.push(
        <option key={-1} value={-1}>
          —
        </option>
      );
    }

    let vals: string[] = [];
    if (start < stop) {
      for (let i = start; i <= stop; i++) {
        vals.push(i.toString());
      }
    } else {
      for (let i = start; i >= stop; i--) {
        vals.push(i.toString());
      }
    }
    for (let i = 0; i < vals.length; i++) {
      let val = vals[i];
      let label = val;
      if (pad) {
        while (val.length < pad) {
          val = '0' + val;
        }
      }
      if (showSmDn) {
        items.push(
          <option key={i} value={val}>
            {label}
          </option>
        );
      } else {
        items.push(
          <MenuItem key={i} value={val}>
            {label}
          </MenuItem>
        );
      }
    }
    return items;
  };

  const createMonths = () => {
    let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    let items: any[] = [];
    if (showSmDn) {
      items.push(
        <option key={-1} value={-1}>
          —
        </option>
      );
    }
    for (let i = 1; i <= 12; i++) {
      let val = i.toString();
      while (val.length < 2) {
        val = '0' + val;
      }
      if (showSmDn) {
        items.push(
          <option key={i} value={val}>
            {months[i - 1]}
          </option>
        );
      } else {
        items.push(
          <MenuItem key={i} value={val}>
            {months[i - 1]}
          </MenuItem>
        );
      }
    }
    return items;
  };

  useEffect(() => {
    const date = splitDateToParts(value);
    setDateParts(date);
  }, []);

  const updateValues = (val: IVal) => {
    let updated: IDateParts = { ...dateParts };
    const key = val.name;
    _.set(updated, `${key}`, val.value);
    const dob = datePartsToDate(updated);
    setDateParts(updated);
    setFieldValue(dateFieldName, dob?.toISOString() || '', true);
  };

  return (
    <>
      <Grid container wrap="nowrap" direction="row">
        <Grid>
          <FormControl sx={{ ...cssClasses.yearField, ...(dateParts.dateYear && cssClasses.touched) }}>
            <InputLabel sx={cssClasses.fieldLabel}>Year</InputLabel>
            {showSmDn ? (
              <NativeSelect
                id="dateYear"
                name="dateYear"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateYear}
                onChange={(val) => updateValues(val.target as IVal)}
              >
                {createNumberSeries(yearRange.stop, yearRange.start)}
              </NativeSelect>
            ) : (
              <Select
                id="dateYear"
                name="dateYear"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateYear}
                onChange={(val) => updateValues(val.target as IVal)}
                data-test="new-patient-year-of-birth-textfield"
              >
                {createNumberSeries(yearRange.stop, yearRange.start)}
              </Select>
            )}

            <ErrorMessage
              component={Typography}
              className={errors.dateYear && touched.dateYear && cssClasses.errorMessage}
              name="dateYear"
            />
          </FormControl>
        </Grid>
        <Grid>
          <FormControl sx={{ ...cssClasses.monthField, ...(dateParts.dateMonth && cssClasses.touched) }}>
            <InputLabel sx={cssClasses.fieldLabel} htmlFor="dateMonth">
              Month
            </InputLabel>
            {showSmDn ? (
              <NativeSelect
                id="dateMonth"
                name="dateMonth"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateMonth}
                onChange={(val) => updateValues(val.target as IVal)}
              >
                {createMonths()}
              </NativeSelect>
            ) : (
              <Select
                id="dateMonth"
                name="dateMonth"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateMonth}
                onChange={(val) => updateValues(val.target as IVal)}
                data-test="new-patient-month-of-birth-textfield"
              >
                {createMonths()}
              </Select>
            )}

            <ErrorMessage component={Typography} className={cssClasses.errorMessage} name="dateMonth" />
          </FormControl>
        </Grid>
        <Grid>
          <FormControl sx={{ ...cssClasses.dateField, ...(dateParts.dateDate && cssClasses.touched) }}>
            <InputLabel sx={cssClasses.fieldLabel} htmlFor="dateDay">
              Day
            </InputLabel>
            {showSmDn ? (
              <NativeSelect
                id="dateDate"
                name="dateDate"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateDate}
                onChange={(val) => updateValues(val.target as IVal)}
              >
                {createNumberSeries(1, 31, 2)}
              </NativeSelect>
            ) : (
              <Select
                id="dateDate"
                name="dateDate"
                variant="outlined"
                sx={cssClasses.selectComponent}
                disabled={readonly}
                value={dateParts.dateDate}
                onChange={(val) => updateValues(val.target as IVal)}
                data-test="new-patient-day-of-birth-textfield"
              >
                {createNumberSeries(1, 31, 2)}
              </Select>
            )}

            <ErrorMessage component={Typography} className={cssClasses.errorMessage} name="dateDay" />
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
}
